import { Briefcase, GraduationCap, Landmark, MapPin, School, ShieldCheck } from 'lucide-react'
import { Link } from 'react-router-dom'
import { AuthLayout } from '../../components/AuthLayout'

export type RegisterRole =
  | 'student'
  | 'tutor'
  | 'guardian'
  | 'school'
  | 'college'
  | 'corporate'

/**
 * Every way into the platform, as one flat list.
 *
 * <p>The entity specs (§3.1) put the three institutions behind a single "I represent an
 * Institution / Company" card that opens a second row. Flattened here deliberately: a school
 * choosing between two nested steps to reach the same form is a click and a guess it does not
 * need, and the six read clearly enough side by side.
 */
/**
 * Which roles the sign-up page offers today - all six.
 *
 * <p>School, College and Corporate were held back while nothing on the learner side could ask for
 * what they offer. They are on now, and the pair still travels together: the three institution
 * chips in {@code lookingFor.ts} were turned on in the same change, because a school that can sign
 * up and no learner who can ask for a school place is the same gap the other way round.
 *
 * <p>Kept as a filter rather than deleted, so hiding one again is a single entry here. This was
 * only ever a display rule: {@code /api/auth/register/institution} has always accepted all three.
 */
const HIDDEN_ROLES: RegisterRole[] = []

const ROLES: { role: RegisterRole; icon: React.ReactNode; title: string; sub: string }[] = [
  {
    role: 'student',
    icon: <GraduationCap size={20} className="text-sun-600" />,
    title: "I'm a Learner",
    sub: 'Find trusted tutors, coaches and trainers - or mentors, funding and co-founders for your startup',
  },
  {
    role: 'tutor',
    icon: <MapPin size={20} className="text-navy-700" />,
    title: "I'm a Tutor / Coach / Trainer",
    sub: 'Teach, mentor, advise or invest - get discovered by nearby learners and founders',
  },
  {
    role: 'guardian',
    icon: <ShieldCheck size={20} className="text-emerald-600" />,
    title: "I'm a Parent / Guardian",
    sub: 'Add your children, approve their connections, see what they see',
  },
  {
    role: 'school',
    icon: <School size={20} className="text-sun-600" />,
    title: "I'm a School",
    sub: 'Put your school on the map for families nearby. Free.',
  },
  {
    role: 'college',
    icon: <Landmark size={20} className="text-navy-700" />,
    title: "I'm a College",
    sub: 'Reach learners choosing where to study next',
  },
  {
    role: 'corporate',
    icon: <Briefcase size={20} className="text-emerald-600" />,
    title: "I'm a Corporate",
    sub: 'Offer internships to learners near you',
  },
]

export function RoleSelectStep({
  onSelect,
  joiningVia,
  only,
}: {
  onSelect: (role: RegisterRole) => void
  /** The organisation whose onboarding link brought them here, named above the choices. */
  joiningVia?: string
  /** Narrows the choices - an aggregator's link brings in learners and tutors only. */
  only?: RegisterRole[]
}) {
  return (
    <AuthLayout wider>
      <h1 className="text-3xl font-extrabold tracking-tight text-ink-900">Join the network</h1>
      <p className="mt-1 text-ink-600">Create your profile in under 3 minutes. It is free.</p>
      {joiningVia && (
        <p className="mt-3 rounded-lg border border-sun-400/60 bg-sun-400/10 px-3 py-2 text-sm text-ink-800">
          You are joining through <span className="font-bold">{joiningVia}</span>.
        </p>
      )}

      {/* Two across, so all six fit on one screen: choosing a role is one look and one click, not a
          scroll through a list. */}
      <div className="mt-5 grid gap-2.5 sm:grid-cols-2">
        {ROLES.filter((r) => !HIDDEN_ROLES.includes(r.role) && (!only || only.includes(r.role))).map((r) => (
          <button
            key={r.role}
            onClick={() => onSelect(r.role)}
            className="card flex w-full items-start gap-3 p-3.5 text-left transition hover:border-sun-400/60 hover:shadow-float"
          >
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ink-100">{r.icon}</span>
            <span>
              <span className="block font-bold leading-snug text-ink-900">{r.title}</span>
              <span className="mt-0.5 block text-[13px] leading-snug text-ink-500">{r.sub}</span>
            </span>
          </button>
        ))}
      </div>

      <p className="mt-4 text-center text-sm text-ink-500">
        Already have an account? <Link to="/login" className="font-semibold text-ink-900 hover:underline">Sign in</Link>
      </p>
    </AuthLayout>
  )
}
